'use client';

/**
 * Browser-side error reporter. Sends client errors to /api/errors/client,
 * which logs them via @west-tennessee-consulting/logger → #Hub Errors Slack channel.
 *
 * Client-safe: no server imports. Uses the session cookie (same-origin), so no
 * token plumbing. Never throws — reporting must not create new errors.
 */

import { isIgnoredClientError } from '@/lib/observability/client-error-schema';
import { isVaultPagePath } from '@/lib/security/vault-page-security';

export interface ClientErrorReport {
  message: string;
  name?: string;
  stack?: string;
  componentStack?: string;
  url?: string;
  digest?: string;
  severity?: 'error' | 'fatal';
  context?: Record<string, unknown>;
}

/** Plain identifier-ish names only (e.g. "TypeError"); anything else is dropped. */
const SAFE_NAME = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;

function pathOf(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return null;
  }
}

/** The page OR the reported url is a credential-vault page (vault review M2). */
function onVaultPage(report: ClientErrorReport): boolean {
  if (isVaultPagePath(window.location.pathname)) return true;
  const reported = pathOf(report.url);
  return reported !== null && isVaultPagePath(reported);
}

/**
 * Defense in depth for `/vault/*`: a vault page's error message, stack, href,
 * rejection reason or context could carry a redemption id, a TOTP code or a
 * revealed value. Send only that SOMETHING failed, with a bare error class name.
 */
export function redactedVaultReport(report: ClientErrorReport): ClientErrorReport {
  return {
    message: '[vault page error — details withheld]',
    ...(report.name && SAFE_NAME.test(report.name) ? { name: report.name } : {}),
    url: `${window.location.origin}/vault`,
    ...(report.severity ? { severity: report.severity } : {}),
  };
}

export function reportClientError(report: ClientErrorReport): void {
  try {
    if (typeof window === 'undefined') return;
    if (isIgnoredClientError(report.message)) return;
    const payload = JSON.stringify(
      onVaultPage(report)
        ? redactedVaultReport(report)
        : { ...report, url: report.url ?? window.location.href },
    );
    // keepalive lets the request survive a navigation / page unload.
    void fetch('/api/errors/client', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: payload,
      keepalive: true,
    }).catch(() => {
      /* swallow — reporting failures must be silent */
    });
  } catch {
    /* never throw from the reporter */
  }
}
